import { useEffect, useState } from "react";

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { bulkRealize } from "@/api/planning";
import { useToast } from "@/components/ToastProvider";

type Props = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  year: number;
  month: number;
};

function lastDayOf(year: number, month: number): string {
  const last = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
}

/**
 * Turn a range of still-forecast shifts into réalisé in one gesture — the
 * month-closing move a coordinator makes before the timesheets go out for
 * validation. Only rows still marked PLANNED are touched, so the corrections
 * already entered by hand survive.
 */
export function CloseMonthDialog({ open, onClose, organizationId, year, month }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDateFrom(`${year}-${String(month).padStart(2, "0")}-01`);
    setDateTo(lastDayOf(year, month));
    setError(null);
  }, [open, year, month]);

  const mutation = useMutation({
    mutationFn: () =>
      bulkRealize(organizationId, year, month, {
        date_from: dateFrom,
        date_to: dateTo,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["planning", organizationId, year, month] });
      qc.invalidateQueries({ queryKey: ["timesheets"] });
      toast(
        t("planning.closeMonthDone", {
          updated: res.updated,
          skipped: res.skipped_locked,
        }),
        res.updated > 0 ? "success" : "info",
      );
      onClose();
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data
        ?.detail;
      setError(typeof detail === "string" ? detail : (e as Error).message);
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("planning.closeMonth")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DialogContentText>{t("planning.closeMonthHelp")}</DialogContentText>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            type="date"
            label={t("planning.dateFrom")}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label={t("planning.dateTo")}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          color="success"
          disabled={mutation.isPending || !dateFrom || !dateTo}
          onClick={() => mutation.mutate()}
        >
          {t("planning.closeMonthConfirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
